import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Catches render errors inside Our Places v2.
 *
 * Without one, a single bad field on a single provider blanks the whole page —
 * and the data here is messy by nature: 300k rows imported from several sources,
 * with inconsistent addresses, missing coordinates and free-text hours from
 * Google. Somebody looking for a provider should never get a white screen because
 * one row was malformed.
 *
 * Deliberately offers a way onward (retry, or back to the landing search) rather
 * than only apologising — the user still needs to find care.
 */
class OurPlacesErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept as console for now: there is no client error-reporting sink yet. When
    // one exists this is the single place to forward from.
    console.error('[OurPlacesV2] render error', error, info?.componentStack);
  }

  handleRetry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="opv2 opv2-errorstate" role="alert">
        <div className="opv2-errorstate__inner">
          <h1 className="opv2-errorstate__title">Something went wrong on this page</h1>
          <p className="opv2-errorstate__body">
            This is our problem, not yours. Your search is still in the address bar, so
            reloading usually works.
          </p>
          <div className="opv2-errorstate__actions">
            <button
              type="button"
              className="opv2-button opv2-button--primary"
              onClick={this.handleRetry}
            >
              Try again
            </button>
            <Link className="opv2-button opv2-button--outline" to="/ourplaces">
              Start a new search
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

export default OurPlacesErrorBoundary;
